import { useMemo } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerCellTriggerButtonProps,
    PickerChevron,
    PickerItemData,
} from "@design-system/Picker/Picker.types";
import {
    buildFlatItems,
    plainFlatItemOptions as plainOptions,
} from "./PickerPlayground.lib";
import {
    PickerCellTableHost,
    PickerViewTableExample,
    type PickerCellHostRow,
} from "./PickerPlaygroundViewTable";
import { PickerSpreadsheetExample } from "./PickerPlaygroundSpreadsheet";
import { VariantGrid, VariantItem } from "./playgroundControls";
import styles from "./PickerPlayground.module.scss";

const ComposedCell = ({
    items,
    defaultValue,
    chevron,
    ...triggerProps
}: {
    items: Array<PickerItemData<string>>;
    defaultValue?: string;
    chevron?: PickerChevron;
    readOnly?: boolean;
    pending?: boolean;
    placeholder?: string;
    showDisabledAdd?: boolean;
    buttonProps?: PickerCellTriggerButtonProps;
}) => (
    <Picker.Select defaultValue={defaultValue ?? null} chevron={chevron}>
        <Picker.CellContent
            {...triggerProps}
            value={items.find((item) => item.value === defaultValue)?.label}
        />
        <Picker.Popup>
            <Picker.List>
                {items.map((item) => (
                    <Picker.Item key={item.value} value={item.value}>
                        {item.label}
                    </Picker.Item>
                ))}
            </Picker.List>
        </Picker.Popup>
    </Picker.Select>
);

const ComposedInputCell = ({
    items,
    defaultValue,
    chevron,
    ...triggerProps
}: {
    items: Array<PickerItemData<string>>;
    defaultValue: PickerItemData<string>;
    chevron?: PickerChevron;
    pending?: boolean;
    clearable?: boolean;
}) => (
    <Picker.Select
        trigger="cellInput"
        chevron={chevron}
        defaultValue={defaultValue.value}
        defaultInputValue={defaultValue.label}
    >
        <Picker.CellInputTrigger {...triggerProps} />
        <Picker.Popup>
            <Picker.List>
                {items.map((item) => (
                    <Picker.Item key={item.value} value={item.value}>
                        {item.label}
                    </Picker.Item>
                ))}
            </Picker.List>
        </Picker.Popup>
    </Picker.Select>
);

/**
 * Cell trigger states inside a real `ViewTable`: rest (no chevron, the
 * legacy cell geometry), an explicit `chevron="hover"`, read-only, pending, the empty "Add"
 * affordance (hidden until its row is hovered, like legacy ViewTable cells)
 * and the cell input variants. Composed cells pass the label as
 * `value`, the way a table cell that owns its row data does. Below it, the
 * bound three-column table and the spreadsheet editor.
 */
export const PickerCellGrid = () => {
    const items = useMemo(() => buildFlatItems(plainOptions), []);
    const rows = useMemo<PickerCellHostRow[]>(
        () => [
            {
                id: "rest",
                label: "Rest",
                cell: (
                    <ComposedCell items={items} defaultValue={items[0].value} />
                ),
            },
            {
                id: "hover",
                label: 'chevron="hover" (hover the cell)',
                cell: (
                    <ComposedCell
                        items={items}
                        defaultValue={items[0].value}
                        chevron="hover"
                    />
                ),
            },
            {
                id: "read-only",
                label: "Read-only",
                cell: (
                    <ComposedCell
                        items={items}
                        defaultValue={items[1].value}
                        readOnly
                    />
                ),
            },
            {
                id: "pending",
                label: "Pending",
                cell: (
                    <ComposedCell
                        items={items}
                        defaultValue={items[2].value}
                        pending
                    />
                ),
            },
            {
                id: "empty",
                label: "Empty (Add: hover the row or focus the cell)",
                cell: <ComposedCell items={items} />,
            },
            {
                id: "read-only-empty",
                label: "Read-only empty · showDisabledAdd",
                cell: (
                    <ComposedCell
                        items={items}
                        readOnly
                        showDisabledAdd
                        buttonProps={{
                            tooltip: "Locked: the period is closed",
                        }}
                    />
                ),
            },
            {
                id: "cell-input",
                label: 'trigger="cellInput"',
                cell: (
                    <Picker.Select
                        items={items}
                        trigger="cellInput"
                        clearable
                        defaultValue={items[0].value}
                    />
                ),
            },
            {
                id: "cell-input-hover",
                label: 'cellInput · chevron="hover" (hover the cell)',
                cell: (
                    <ComposedInputCell
                        items={items}
                        defaultValue={items[0]}
                        chevron="hover"
                        clearable
                    />
                ),
            },
            {
                id: "cell-input-pending",
                label: "cellInput · pending",
                cell: (
                    <ComposedInputCell
                        items={items}
                        defaultValue={items[2]}
                        pending
                    />
                ),
            },
            {
                id: "cell-input-custom",
                label: "cellInput · allowCustomValue",
                cell: (
                    <Picker.Select
                        items={items}
                        trigger="cellInput"
                        allowCustomValue
                        defaultValue={items[1].label}
                        clearable
                    />
                ),
            },
        ],
        [items],
    );

    return (
        <VariantGrid title="Cell triggers in real tables" columns="wide">
            <VariantItem label="Cell trigger states (ViewTable rows)">
                <PickerCellTableHost rows={rows} />
            </VariantItem>
            <VariantItem label="Open (popup drawn inline)">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        items={items}
                        defaultValue={items[0].value}
                    />
                </div>
            </VariantItem>
            <VariantItem label='ViewTable · trigger="cell" and trigger="cellInput" columns'>
                <PickerViewTableExample />
            </VariantItem>
            <VariantItem label="SpreadsheetGrid · Picker cell editor (Enter / type to edit, Escape to leave)">
                <PickerSpreadsheetExample />
            </VariantItem>
        </VariantGrid>
    );
};
